import { useState, useCallback, useContext } from 'react';
import { useHistory } from 'react-router-dom';
import {
    getFirstError,
    getErrorMessage,
    showErrorNotificationFromResponse,
} from '../../../services/errorUtils';
import ResponseStatus from '../../../constants/ResponseStatus';
import ResponseErrorTypes from '../../../constants/ResponseErrorTypes';
import { changePassword } from '../myAccountApi';
import ChangePasswordDesktop from './ChangePasswordDesktop';
import { isMobile } from '../../../services/DeviceInformation';
import useChangePassword from '../../../services/useChangePassword';
import useNetworkErrorNotification from '../../../services/useNetworkErrorNotification';
import ChangePasswordMobile from './ChangePasswordMobile';
import ChangePasswordContext from './ChangePasswordContext';
import { useUserSessionContext } from '../../../components/UserSessionContextWrapper';
import InsecurePasswordMessage from '../../../components/InsecurePasswordMessage';
import LoadingScreenContext from '../../../services/LoadingScreenContext';
import AuthenticatedRouteLanguageContext from '../../../services/AuthenticatedRouteLanguageContext';
import { AuthenticatedPaths } from '../../../constants/Paths';

function ChangePassword() {
    const { messageLanguage } = useContext(AuthenticatedRouteLanguageContext);
    const [currentPasswordError, setCurrentPasswordError] = useState('');
    const [newPasswordError, setNewPasswordError] = useState('');
    const [confirmPasswordError, setConfirmPasswordError] = useState('');
    const [allRequirementsMet, setAllRequirementsMet] = useState(false);
    const [showSuccessView, setShowSuccessView] = useState(false);
    const { setLoading } = useContext(LoadingScreenContext);
    const {
        currentPassword,
        setCurrentPassword,
        newPassword,
        setNewPassword,
        confirmedPassword,
        setConfirmedPassword,
    } = useChangePassword();
    const history = useHistory();
    const { handleResponseSessionErrors } = useUserSessionContext();
    const { showNotification } = useNetworkErrorNotification();

    const handleErrorResponse = async (response) => {
        const error = getFirstError(response);
        const errorMessage = await getErrorMessage(error, messageLanguage);
        switch (error.type) {
            case ResponseErrorTypes.VEEVAID_INVALID_USER_PASSWORD: {
                setCurrentPasswordError(errorMessage);
                break;
            }
            case ResponseErrorTypes.VEEVAID_INVALID_NEW_PASSWORD: {
                setNewPasswordError(errorMessage);
                break;
            }
            case ResponseErrorTypes.VEEVAID_INSECURE_PASSWORD: {
                setConfirmPasswordError(<InsecurePasswordMessage />);
                break;
            }
            default: {
                error.message = errorMessage;
                const i18nOptions = { language: messageLanguage };
                await showErrorNotificationFromResponse(response, i18nOptions);
            }
        }
    };

    const handleSubmitPassword = async () => {
        setLoading(true);
        const response = await changePassword(currentPassword, newPassword);
        setLoading(false);

        if (handleResponseSessionErrors(response)) {
            return;
        }

        switch (response.responseStatus) {
            case ResponseStatus.SUCCESS:
                setConfirmedPassword('');
                setNewPassword('');
                setCurrentPassword('');
                setNewPasswordError('');
                setConfirmPasswordError('');
                setCurrentPasswordError('');
                setShowSuccessView(true);
                break;
            case ResponseStatus.FAILURE:
                await handleErrorResponse(response);
                break;
            case ResponseStatus.UI_NETWORK_ERROR:
                await showNotification();
                break;
            default:
            // No-op
        }
    };

    const handleValidatorStateChange = useCallback(
        ({ valid, inlineError }) => {
            setAllRequirementsMet(valid && !inlineError && !!currentPassword);
            setNewPasswordError(inlineError);
            setConfirmPasswordError('');
        },
        [currentPassword],
    );

    const handleCurrentPasswordChange = (value) => {
        setCurrentPassword(value);
        setCurrentPasswordError('');
    };

    const handleNewPasswordChange = (value) => setNewPassword(value);
    const handleNewPasswordConfirmChange = (value) => setConfirmedPassword(value);
    const handleBackButtonClick = () => history.push(AuthenticatedPaths.MY_ACCOUNT);

    return (
        <ChangePasswordContext.Provider
            value={{
                confirmPasswordError,
                currentPassword,
                currentPasswordError,
                disableSubmitButton: !allRequirementsMet,
                newPassword,
                newPasswordConfirm: confirmedPassword,
                newPasswordError,
                onCurrentPasswordChange: handleCurrentPasswordChange,
                onNewPasswordChange: handleNewPasswordChange,
                onNewPasswordConfirmChange: handleNewPasswordConfirmChange,
                onSubmitButtonClick: handleSubmitPassword,
                onBackButtonClick: handleBackButtonClick,
                onValidatorStateChange: handleValidatorStateChange,
                showSuccessView,
            }}
        >
            {isMobile() ? <ChangePasswordMobile /> : <ChangePasswordDesktop />}
        </ChangePasswordContext.Provider>
    );
}

export default ChangePassword;
